import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Components/Sidebar.tsx';

type AdminDetails = {
  AdminID: number;
  FullName: string;
  Username: string;
  Email: string;
  ProfilePhoto: string | null;
  CreatedAt: string;
  UpdatedAt: string;
};

const AdminScreen: React.FC = () => {
  const [admin, setAdmin] = useState<AdminDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    FullName: '',
  });
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  // Fetch admin details
  useEffect(() => {
    const fetchAdmin = async () => {
      try {
        // 🔴 Replace '1' with your actual admin ID (hardcoded for now)
        const ADMIN_ID = '1';
        
        const response = await fetch(`http://kkk.biznweb.in:5000/api/get-admin/${ADMIN_ID}`);
        if (!response.ok) throw new Error('Failed to fetch admin details');
        
        const data = await response.json();
        if (data.success && data.admin) {
          setAdmin(data.admin);
          setFormData({ 
            FullName: data.admin.FullName || '', 
          });
          setPreviewUrl(data.admin.ProfilePhoto || null);
        } else {
          setError(data.message || 'No admin data found');
        }
      } catch (err: any) {
        setError(err.message || 'Error fetching admin details');
      } finally {
        setLoading(false);
      }
    };

    fetchAdmin();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfilePhoto(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('FullName', formData.FullName);
      
      if (profilePhoto) {
        formDataToSend.append('ProfilePhoto', profilePhoto);
      }

      const response = await fetch(`http://kkk.biznweb.in:5000/api/update-admin/${admin?.AdminID}`, {
        method: 'PUT',
        body: formDataToSend,
      });

      const result = await response.json();

      if (result.success) {
        alert('Admin details updated successfully!');
        setIsEditing(false);
        setAdmin(prev => prev ? { 
          ...prev, 
          FullName: formData.FullName,
          ProfilePhoto: result.file || prev.ProfilePhoto 
        } : null);
      } else {
        alert(result.message || 'Failed to update admin');
      }
    } catch (err: any) {
      alert(err.message || 'Error updating admin details');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', height: '100vh', background: '#f8fafc' }}>
        <Sidebar />
        <div style={{ flex: 1, padding: '24px', marginLeft: '280px' }}>
          <div style={{ textAlign: 'center', padding: '40px', color: '#1976d2' }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (error || !admin) {
    return (
      <div style={{ display: 'flex', height: '100vh', background: '#f8fafc' }}>
        <Sidebar />
        <div style={{ flex: 1, padding: '24px', marginLeft: '280px' }}>
          <div style={{ color: 'crimson', padding: '20px' }}>{error || 'No admin data found'}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
      <Sidebar />
      <div style={{ flex: 1, padding: '24px', marginLeft: '280px' }}>
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontSize: '26px', fontWeight: 700, color: '#0f172a', margin: 0 }}>Admin Profile</h1>
        </div>

        <div style={{
          background: '#fff',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          {/* Profile Section */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ 
              width: '120px', 
              height: '120px', 
              borderRadius: '50%', 
              overflow: 'hidden', 
              margin: '0 auto 16px',
              border: '4px solid #e2e8f0'
            }}>
              <img
                src={previewUrl || admin.ProfilePhoto ? 
                  admin.ProfilePhoto : 
                  "/assets/default-profile.jpg"
                }
                alt="Profile"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "/assets/default-profile.jpg";
                }}
              />
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: '20px', color: '#333' }}>
              {isEditing ? (
                <input
                  type="text"
                  name="FullName"
                  value={formData.FullName}
                  onChange={handleInputChange}
                  placeholder="Full Name"
                  style={{
                    padding: '8px 12px',
                    fontSize: '18px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    width: '100%',
                    maxWidth: '300px',
                    textAlign: 'center'
                  }}
                />
              ) : (
                admin.FullName
              )}
            </h2>
            <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
              Admin ID: {admin.AdminID}
            </p>
          </div>

          {/* Form Section */}
          {isEditing && (
            <form onSubmit={handleSubmit} style={{ marginBottom: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 500, 
                  color: '#334155' 
                }}>
                  Full Name
                </label>
                <input
                  type="text"
                  name="FullName"
                  value={formData.FullName}
                  onChange={handleInputChange}
                  placeholder="Enter full name"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 500, 
                  color: '#334155' 
                }}>
                  Upload New Photo
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ width: '100%', padding: '8px' }}
                />
                {previewUrl && (
                  <div style={{ marginTop: '12px', textAlign: 'center' }}>
                    <p style={{ margin: '0 0 8px', fontSize: '14px', color: '#64748b' }}>
                      Preview:
                    </p>
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{ 
                        width: '100px', 
                        height: '100px', 
                        borderRadius: '50%', 
                        objectFit: 'cover',
                        border: '2px solid #e2e8f0'
                      }}
                    />
                  </div>
                )}
              </div>
            </form>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                style={{
                  padding: '10px 20px',
                  background: '#1e40af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: '14px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#1d4ed8'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#1e40af'}
              >
                Edit Profile
              </button>
            ) : (
              <>
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isSaving}
                  style={{
                    padding: '10px 20px',
                    background: isSaving ? '#94a3b8' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    fontWeight: 500,
                    fontSize: '14px',
                    transition: 'background 0.2s'
                  }}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setFormData({ 
                      FullName: admin.FullName,
                    });
                    setProfilePhoto(null);
                    setPreviewUrl(admin.ProfilePhoto || null);
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '14px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#dc2626'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#ef4444'}
                >
                  Cancel
                </button>
              </>
            )}
          </div>

          {/* Admin Details */}
          <div style={{ marginTop: '24px', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: '#334155' }}>Account Details</h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Created:</span>
                <span style={{ color: '#334155' }}>
                  {new Date(admin.CreatedAt).toLocaleString()}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Last Updated:</span>
                <span style={{ color: '#334155' }}>
                  {admin.UpdatedAt ? new Date(admin.UpdatedAt).toLocaleString() : 'Never'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminScreen;